import { questions, SECTION_WEIGHT, type AgeRange, type LifeStage, type Readiness, type RejectionTag, type RecentExperience, type TraitKey } from '@/data/questions';

// =============================================================
// TonyKnows v2 scoring engine
// 7 traits + structured constraint extraction + legacy mapping
// so the existing results UI keeps working without a redesign.
// =============================================================

export type { LifeStage, RejectionTag, AgeRange, Readiness };

export interface RawTraits {
  analytical: number;
  creative: number;
  social: number;
  physical: number;
  structure: number;
  risk: number;
  income: number;
}

export interface UserConstraints {
  lifeStage: LifeStage;
  ageRange?: AgeRange;
  readiness?: Readiness;
  studyTolerance: 'none' | 'short' | 'medium' | 'long';
  riskTolerance: 'high' | 'medium' | 'low' | 'avoid';
  pressureTolerance: 'high' | 'medium' | 'low' | 'avoid';
  securityNeed: 'high' | 'medium' | 'low' | 'none';
  avoidsPhysical: boolean;
  avoidsOutdoor: boolean;
  needsRegularHours: boolean;
  rejections: RejectionTag[];
}

// Legacy 5-trait shape (+risk) the existing results UI imports.
export interface TraitScores {
  people: number;
  creativity: number;
  income: number;
  freedom: number;
  structure: number;
  risk: number;
}

export interface ContextSignals {
  lifeStage: LifeStage;
  recentExperience: RecentExperience;
}

const TRAIT_KEYS: TraitKey[] = ['analytical','creative','social','physical','structure','risk','income'];

// =============================================================
// Aggregate per-option deltas, weighted by section weight.
// Each trait is normalised on a 0-100 scale around a baseline of 50.
// =============================================================
export function calculateRawTraits(answers: Record<number, number[]>): RawTraits {
  const sums: Record<TraitKey, number> = {
    analytical: 0, creative: 0, social: 0, physical: 0,
    structure: 0, risk: 0, income: 0,
  };
  const weights: Record<TraitKey, number> = {
    analytical: 0, creative: 0, social: 0, physical: 0,
    structure: 0, risk: 0, income: 0,
  };

  // Light-touch teen adjustment: for 13-17 users we lean the model slightly
  // toward interest/preference signals and away from work-experience-coded
  // sections. We do NOT branch the questionnaire - just nudge weights.
  const ageOpt = answers[25]?.[0];
  const ageQ = questions.find(q => q.id === 25);
  const ageRange = ageOpt != null ? ageQ?.options[ageOpt]?.filters?.ageRange : undefined;
  const isTeen = ageRange === '13-17';

  const TEEN_SECTION_MULT: Partial<Record<typeof questions[number]['category'], number>> = {
    tendencies: 1.3,   // interests / what they naturally lean toward
    environment: 1.15, // preferences for setting / routine
    workstyle: 0.85,   // work-coded behaviour
    constraints: 0.85, // job-security / risk / study tolerance
    tradeoff: 0.9,
  };

  // Safeguard: cap the combined influence of boosted categories
  // (tendencies + environment) to ~45% of the total weighting pool, so
  // a teen's interest signals never completely drown out the other
  // dimensions. If the cap would be exceeded we soften the boosts
  // proportionally rather than letting them runaway.
  if (isTeen) {
    let boostedW = 0;
    let totalW = 0;
    for (const q of questions) {
      if (q.category === 'lifestage' || q.category === 'rejection' || q.category === 'age' || q.category === 'readiness') continue;
      const baseW = SECTION_WEIGHT[q.category] || 0;
      const sectionW = baseW * (TEEN_SECTION_MULT[q.category] ?? 1);
      totalW += sectionW;
      if (q.category === 'tendencies' || q.category === 'environment') boostedW += sectionW;
    }
    const MAX_SHARE = 0.45;
    if (totalW > 0 && boostedW / totalW > MAX_SHARE) {
      const targetBoosted = totalW * MAX_SHARE;
      const scale = targetBoosted / boostedW;
      TEEN_SECTION_MULT.tendencies = (TEEN_SECTION_MULT.tendencies ?? 1) * scale;
      TEEN_SECTION_MULT.environment = (TEEN_SECTION_MULT.environment ?? 1) * scale;
    }
  }

  for (const q of questions) {
    if (q.category === 'lifestage' || q.category === 'rejection') continue;
    const baseW = SECTION_WEIGHT[q.category] || 0;
    if (baseW === 0) continue;
    const sectionW = isTeen
      ? baseW * (TEEN_SECTION_MULT[q.category] ?? 1)
      : baseW;

    const selected = answers[q.id];
    if (!selected || selected.length === 0) continue;

    for (const idx of selected) {
      const opt = q.options[idx];
      if (!opt?.deltas) continue;
      for (const k of TRAIT_KEYS) {
        const d = opt.deltas[k];
        if (typeof d === 'number') {
          sums[k] += d * sectionW;
          weights[k] += sectionW;
        }
      }
    }
  }

  const out = {} as RawTraits;
  for (const k of TRAIT_KEYS) {
    // Convert weighted delta sum into a 0-100 score around a baseline of 50.
    const avgDelta = weights[k] === 0 ? 0 : sums[k] / weights[k];
    out[k] = Math.max(0, Math.min(100, Math.round(50 + avgDelta * 1.2)));
  }
  return out;
}

// =============================================================
// Pull hard / soft constraints straight from the answers.
// =============================================================
export function extractConstraints(answers: Record<number, number[]>): UserConstraints {
  let lifeStage: LifeStage = 'between';
  let ageRange: AgeRange | undefined;
  let readiness: Readiness | undefined;
  let studyTolerance: UserConstraints['studyTolerance'] = 'medium';
  let riskTolerance: UserConstraints['riskTolerance'] = 'medium';
  let pressureTolerance: UserConstraints['pressureTolerance'] = 'medium';
  let securityNeed: UserConstraints['securityNeed'] = 'medium';
  let avoidsPhysical = false;
  let needsRegularHours = false;
  const rejections: RejectionTag[] = [];

  for (const q of questions) {
    const selected = answers[q.id];
    if (!selected) continue;
    for (const idx of selected) {
      const f = q.options[idx]?.filters;
      if (!f) continue;
      if (f.lifeStage) lifeStage = f.lifeStage;
      if (f.ageRange) ageRange = f.ageRange;
      if (f.readiness) readiness = f.readiness;
      if (f.studyTolerance) studyTolerance = f.studyTolerance;
      if (f.riskTolerance) riskTolerance = f.riskTolerance;
      if (f.pressureTolerance) pressureTolerance = f.pressureTolerance;
      if (f.securityNeed) securityNeed = f.securityNeed;
      if (f.avoidsPhysical) avoidsPhysical = true;
      if (f.needsRegularHours) needsRegularHours = true;
      if (f.rejectionTag && !rejections.includes(f.rejectionTag)) {
        rejections.push(f.rejectionTag);
      }
    }
  }

  // Derive avoidsOutdoor from the workday question (id=1: sitting/focusing indoors)
  // and Q4 (avoids physical).
  const workday = answers[1]?.[0];
  const avoidsOutdoor = workday === 0 || avoidsPhysical;

  return {
    lifeStage,
    ageRange,
    readiness,
    studyTolerance,
    riskTolerance,
    pressureTolerance,
    securityNeed,
    avoidsPhysical,
    avoidsOutdoor,
    needsRegularHours,
    rejections,
  };
}

// =============================================================
// Map RawTraits -> legacy TraitScores so existing results UI keeps working.
// people = social, creativity = creative, income = income,
// structure = structure, risk = risk, freedom = inverse(structure) + creativity bias.
// =============================================================
function toLegacyTraits(r: RawTraits): TraitScores {
  const clamp = (n: number) => Math.max(0, Math.min(100, Math.round(n)));
  const freedom = 100 - r.structure * 0.6 + (r.creative - 50) * 0.2;
  return {
    people:     clamp(r.social),
    creativity: clamp(r.creative),
    income:     clamp(r.income),
    freedom:    clamp(freedom),
    structure:  clamp(r.structure),
    risk:       clamp(r.risk),
  };
}

export function calculateTraits(answers: Record<number, number[]>): TraitScores {
  return toLegacyTraits(calculateRawTraits(answers));
}

export function calculateRawTraitsPublic(answers: Record<number, number[]>): RawTraits {
  return calculateRawTraits(answers);
}

// =============================================================
// Profile summary
// =============================================================
export function generateProfileSummary(
  traits: TraitScores,
  _ctx?: ContextSignals,
  opts?: { ageRange?: AgeRange },
): string {
  const parts: string[] = [];
  if (traits.people > 65) parts.push('people-focused');
  else if (traits.people < 35) parts.push('independently-minded');
  if (traits.creativity > 65) parts.push('creative');
  if (traits.income > 65) parts.push('income-driven');
  if (traits.freedom > 65) parts.push('freedom-oriented');
  else if (traits.structure > 65) parts.push('structured');
  if (parts.length === 0) parts.push('well-rounded');

  const isTeen = opts?.ageRange === '13-17';
  if (isTeen) {
    return `Based on your answers, you come across as ${parts.join(', ')}. The careers below are paths that could be a great fit to explore - none of this is set in stone, it's a starting point. You can revisit this as your interests and experience grow.`;
  }
  return `Based on your answers, you come across as ${parts.join(', ')}. The careers below are ranked by how well they match your real-world preferences and constraints.`;
}

// Legacy stub - context is now inside UserConstraints (lifeStage).
export function extractContext(answers: Record<number, number[]>): ContextSignals {
  const constraints = extractConstraints(answers);
  return { lifeStage: constraints.lifeStage, recentExperience: 'unsure' };
}

export function getGuidanceTone(ctx: ContextSignals) {
  // Slightly different tone per life stage.
  switch (ctx.lifeStage) {
    case 'school':
    case 'sixthform':
      return { stepPrefix: 'Your next step:', urgency: 'exploratory' as const };
    case 'university':
    case 'college':
      return { stepPrefix: 'Your next step:', urgency: 'preparation' as const };
    case 'working':
      return { stepPrefix: 'Your next step:', urgency: 'transition' as const };
    case 'between':
    default:
      return { stepPrefix: 'Your next step:', urgency: 'actionable' as const };
  }
}
